import React, { useEffect, useMemo, useState, useContext } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender,
} from '@tanstack/react-table';
import { FilterContext } from '../../context/Filter';
import { axInstBasic } from '../Service/Service';

// Utility: format in thousands with ₹ symbol
const formatInThousands = (num) => {
    if (num === null || num === undefined) return '₹0.00 K';
    return `₹${(num / 1000).toFixed(2)} K`;
};

export default function TanStackTable() {
    const [claimPopData, setClaimPopData] = useState([]);
    const { arn: parArn, childArn } = useContext(FilterContext);
    const arn = parArn || childArn;
    const key = childArn ? 'childArn' : 'parArn';
    const payload = { [key]: arn };

    // Fetch popup data from backend
    const fetchClaimPopup = async () => {
        try {
            const res = await axInstBasic.post(
                `${process.env.REACT_APP_API_URL}claimsAndDiscountPopUpTable`,
                payload
            );
            return res.data.result;
        } catch (error) {
            console.error('Error fetching claim popup:', error);
            return null;
        }
    };

    // Fetch and transform data for table
    useEffect(() => {
        window.scrollTo(0, 0);

        const fetchData = async () => {
            const popupData = await fetchClaimPopup();
            if (!popupData) return;

            // Transform backend data into table rows
            const mappedData = [
                {
                    serviceType: 'Goodwill Commercial',
                    vehicles: popupData?.goodwillCommercialData?.Total_Vehicles?.value ?? 0,
                    claims: popupData?.goodwillCommercialData?.Total_JC?.value ?? 0,
                    totalAmount: popupData?.goodwillCommercialData?.Total?.value ?? 0,
                },
                {
                    serviceType: 'Goodwill Technical',
                    vehicles: popupData?.goodwillTechnicalData?.Total_Vehicles?.value ?? 0,
                    claims: popupData?.goodwillTechnicalData?.Total_JC?.value ?? 0,
                    totalAmount: popupData?.goodwillTechnicalData?.Total?.value ?? 0,
                },
                {
                    serviceType: 'Dealer Empowered',
                    vehicles: popupData?.dealerEmpGoodwillData?.Total_Vehicles?.value ?? 0,
                    claims: popupData?.dealerEmpGoodwillData?.Total_JC?.value ?? 0,
                    totalAmount: popupData?.dealerEmpGoodwillData?.Total?.value ?? 0,
                },
                {
                    serviceType: 'Warranty',
                    vehicles: popupData?.warrantyRowData?.Total_Vehicles?.value ?? 0,
                    claims: popupData?.warrantyRowData?.Total_JC?.value ?? 0,
                    totalAmount: popupData?.warrantyRowData?.Total?.value ?? 0,
                },
                {
                    serviceType: 'JC Parts Discount',
                    vehicles: popupData?.jcPartsDisscountData?.Total_Vehicles?.value ?? 0,
                    claims: popupData?.jcPartsDisscountData?.Total_JC?.value ?? 0,
                    totalAmount:
                        popupData?.jcPartsDisscountData?.total?.value ??
                        popupData?.jcPartsDisscountData?.spares_header_dscnt?.value ??
                        0,
                },
            ];

            setClaimPopData(mappedData);
            // console.log('Mapped claim popup data:', mappedData);
        };

        if (arn) fetchData();
    }, [arn]);

    // Table columns
    const columns = useMemo(
        () => [
            {
                accessorKey: 'serviceType',
                header: 'Service Type',
            },
            {
                accessorKey: 'vehicles',
                header: 'Vehicles',
            },
            {
                accessorKey: 'claims',
                header: 'Claims/JC Count/NAF Count',
            },
            {
                accessorKey: 'totalAmount',
                header: 'Total Claim/Discount Amount',
                cell: (info) => formatInThousands(info.getValue()),
            },
        ],
        []
    );

    const table = useReactTable({
        data: claimPopData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    return (
        <div className="w-full max-w-4xl mx-auto mt-4">
            <div className="text-white p-2 bg-gradient-to-b from-[#88cfed] to-[#217EFF] opacity-80 font-bold text-center w-full">
                Customer Discount & Details
            </div>

            <table className="border border-gray-900 w-full">
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <th
                                    key={header.id}
                                    onClick={header.column.getToggleSortingHandler()}
                                    className="p-3 text-sm border border-gray-900 text-white bg-[#2374BF] text-center cursor-pointer"
                                >
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                    {{
                                        asc: ' 🔼',
                                        desc: ' 🔽',
                                    }[header.column.getIsSorted()] ?? null}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map((row) => (
                        <tr
                            key={row.id}
                            className="border border-gray-900 text-xs text-center text-gray-700 even:bg-gray-50"
                        >
                            {row.getVisibleCells().map((cell) => (
                                <td key={cell.id} className="border border-gray-900 p-2">
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>

            {claimPopData.length === 0 && (
                <div className="text-center text-sm text-gray-500 mt-2">Loading data...</div>
            )}
        </div>
    );
}
